/**
 * Aide de configuration Ringover — correspondance entre les champs du CRM et
 * l'endroit exact où lire la valeur dans le dashboard Ringover.
 *
 * Uniquement Ringover : les autres fournisseurs n'ont pas de guide ici.
 * Les libellés du dashboard changent avec sa langue, d'où la forme
 * « Français (English) ». Chemins vérifiés sur le centre d'aide Ringover
 * (voir DOCS ci-dessous) — à revoir si Ringover réorganise son dashboard.
 */

export const RINGOVER_DOCS = {
  dashboard: "https://dashboard.ringover.com/",
  apiKey: "https://support.ringover.com/hc/en-us/articles/15206971712657-Using-our-API",
  userId: "https://support.ringover.com/hc/en-us/articles/15788910600593-Ringover-User-ID-API-key",
  webhooks: "https://support.ringover.com/hc/en-us/articles/15206913970577-Using-our-webhooks",
  developer: "https://developer.ringover.com/",
};

/** Ordre de mise en service, affiché au-dessus du formulaire. */
export const RINGOVER_STEPS = [
  {
    title: "Créer la clé API chez Ringover",
    detail: "Dashboard Ringover → Développeur (Developer) → Clé API (API key) → « Créer une clé API ». Activez les droits de lecture (utilisateurs, numéros, appels) et le droit d'appel sortant.",
  },
  {
    title: "Coller la clé dans « API Key » ci-dessous",
    detail: "La clé se recopie telle quelle : majuscules et minuscules comptent, et il ne faut pas ajouter « Bearer » devant.",
  },
  {
    title: "Déclarer l'URL de webhook du CRM",
    detail: "Dashboard Ringover → Développeur (Developer) → Webhooks : collez l'URL affichée plus bas dans chaque événement à recevoir (appels entrants, appels manqués, appels répondus, messagerie).",
  },
  {
    title: "Coller le secret de signature, puis tester",
    detail: "Reportez le secret du webhook dans « Webhook Secret » puis lancez « Tester la connexion » : la frise de droite indique quelle étape bloque.",
  },
];

/**
 * Une entrée par clé de CONFIG_FIELDS du RingoverProvider (backend).
 * `path` = fil d'Ariane exact dans le dashboard, `equivalent` = le champ
 * Ringover correspondant, `note` = le piège à éviter.
 */
export const RINGOVER_FIELD_HELP = {
  api_key: {
    path: ["Dashboard Ringover", "Développeur (Developer)", "Clé API (API key)", "Créer une clé API"],
    equivalent: "La valeur affichée sous « API key » une fois la clé créée.",
    note: "Sans les droits cochés à la création, la connexion répond 401 même avec la bonne clé. Une clé n'est affichée en clair qu'à sa création : régénérez-la si elle a été perdue.",
    doc: { label: "Using our API — centre d'aide Ringover", url: RINGOVER_DOCS.apiKey },
  },
  webhook_secret: {
    path: ["Dashboard Ringover", "Développeur (Developer)", "Webhooks"],
    equivalent: "Le secret de signature (signature V3) associé au webhook que vous créez.",
    note: "Champ non marqué obligatoire, mais requis en pratique : sans lui, le CRM refuse tous les événements Ringover (401). Il sert à vérifier les en-têtes X-Ringover-Webhook-Signature-V3 et X-Ringover-Request-Signature-V3-Timestamp.",
    doc: { label: "Using our webhooks — centre d'aide Ringover", url: RINGOVER_DOCS.webhooks },
  },
  base_url: {
    path: null,
    equivalent: "Aucun champ à recopier : c'est la région d'hébergement de votre compte Ringover.",
    note: "public-api.ringover.com/v2 = compte européen (cas courant, valeur par défaut de la documentation Ringover). L'adresse en -us ne concerne qu'un compte hébergé aux États-Unis. Une clé européenne envoyée sur l'URL américaine répond 401 : dans le doute, gardez la valeur par défaut.",
    doc: { label: "Documentation API Ringover", url: RINGOVER_DOCS.developer },
  },
  account_id: {
    path: ["Dashboard Ringover", "Utilisateurs (Users)"],
    equivalent: "Le « User number » affiché sur la fiche d'un utilisateur (ex. 137879).",
    note: "Champ purement indicatif : le CRM ne l'envoie dans aucun appel API aujourd'hui. Laissez-le vide si vous n'avez qu'un compte Ringover.",
    doc: { label: "Ringover User ID & API key — centre d'aide", url: RINGOVER_DOCS.userId },
  },
  default_from_number: {
    path: ["Dashboard Ringover", "Numéros (Numbers)"],
    equivalent: "Un des numéros de la liste, recopié au format international (+33…).",
    note: "Doit être un numéro Ringover actif du compte : un numéro qui n'appartient pas au compte fait échouer l'appel sortant.",
    doc: { label: "Documentation API Ringover", url: RINGOVER_DOCS.developer },
  },
  default_device: {
    path: null,
    equivalent: "Aucun champ à recopier : c'est le paramètre « device » de l'appel POST /callback de l'API Ringover.",
    note: "ALL fait sonner tous les appareils de l'agent, APP l'application desktop, WEB le navigateur, SIP un poste SIP, MOB le mobile, EXT un numéro externe. En cas de doute, gardez ALL.",
    doc: { label: "Documentation API Ringover", url: RINGOVER_DOCS.developer },
  },
};

/** Aide affichée à côté de l'URL de webhook générée par le CRM. */
export const RINGOVER_WEBHOOK_HELP = {
  path: ["Dashboard Ringover", "Développeur (Developer)", "Webhooks"],
  note: "Collez cette même URL dans chaque événement à recevoir (appels entrants, manqués, répondus, messagerie). Ringover doit pouvoir l'atteindre depuis internet : une adresse locale ou en HTTP simple ne recevra rien.",
  doc: { label: "Using our webhooks — centre d'aide Ringover", url: RINGOVER_DOCS.webhooks },
};

export const isRingover = (type) => (type || "").toUpperCase() === "RINGOVER";
